import React, { useCallback, useMemo, useEffect } from 'react';
import styles from '@/interface/ui/launch/index.module.scss';
import Items from '@/interface/ui/_shared/items/index';
import Menu from '@/interface/ui/_shared/items/menu';
import Layout from '@/interface/ui/_shared/layout/index';
import type { RootState } from '@/store/index';
import type { NextPage } from 'next';
import Head from 'next/head';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useCachedScrollPosition } from '@/hooks/useCachedScrollPosition';
import { useShortcuts } from '@/hooks/useShortcuts';
import { ShortcutsBar } from '@/interface/ui/_shared/common/ShortcutsBar';
import { useLaunch } from '@/hooks/useLaunch';
import Shortcut from '@/domain/shortcuts';
import { useEvents } from '@/hooks/useEvents';
import { ShortcutsResultTitle } from '@/interface/ui/_shared/common/ShortcutsResultTitle';
import { Modal } from '../_shared/modal';
import { useOS } from '@/hooks/useOS';
import { useResponsive } from '@/hooks/useResponsive';

interface Props {
  mode?: 'default';
}

const meta = {
  title: 'atmos LISTs | ローンチ',
  description: 'atmos LISTs | ローンチ',
};

const Launch: NextPage<Props> = ({ mode }) => {
  useCachedScrollPosition();

  const { groupedLaunch, fetchLaunchAsync, importLaunchData } = useLaunch();
  const { events, groupedEvents } = useEvents();

  const {
    fetchItemsAsync: fetchShortcutItemsAsync,
    shortcuts,
    activeShortcut,
    clearActiveShortcut,
    itemCount,
  } = useShortcuts('launch');
  const [eventShortcutSelected, setEventShortcutSelected] = useState(false);

  const { isDesktop } = useResponsive();
  const [rowNumber, setRowNumber] = useState(2);

  const { userAgent } = useSelector((state: RootState) => state.app);

  const fetchShortcutItems = useCallback(
    async (shortcut: Shortcut) => {
      const data = await fetchShortcutItemsAsync(shortcut);

      if (data) {
        importLaunchData({ ...data, usingShortcuts: true });
        setEventShortcutSelected(false);
      }
    },
    [fetchShortcutItemsAsync, importLaunchData]
  );

  const clearShortcutFilter = useCallback(async () => {
    await fetchLaunchAsync();
    clearActiveShortcut();
    setEventShortcutSelected(false);
  }, [clearActiveShortcut, fetchLaunchAsync]);

  const onPressShortcut = useCallback(
    async (shortcut: Shortcut) => {
      if (activeShortcut?.id === shortcut.id) {
        void clearShortcutFilter();
        setEventShortcutSelected(false);
        return;
      }

      await fetchShortcutItems(shortcut);
    },
    [activeShortcut?.id, clearShortcutFilter, fetchShortcutItems]
  );

  const onPressEventShortcut = useCallback(() => {
    clearActiveShortcut();
    setEventShortcutSelected(true);
  }, [clearActiveShortcut]);

  const [showModal, setShowModal] = useState(false);
  const { osName } = useOS();

  const handleStarClick = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);

  const renderModal = useCallback(() => {
    return (
      <Modal
        title={<p />}
        onPress={() => setShowModal(false)}
        isStoreLink
        osType={osName}
      >
        <></>
      </Modal>
    );
  }, [osName]);

  useEffect(() => {
    void fetchLaunchAsync();

    // 初期読み込み
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 検索結果のカウント表示用
  // item countにはイベントの数を含まないためここで計算
  const launchItemCount = useMemo(() => {
    let count = 0;

    Object.keys(groupedLaunch).map((groupingKey) => {
      count = count + groupedLaunch[groupingKey].length;
    });

    return Math.max(itemCount || 0, count);
  }, [groupedLaunch, itemCount]);

  const renderLaunchItem = useCallback(() => {
    const groupedData = eventShortcutSelected ? groupedEvents : groupedLaunch;

    return (
      <ul className={styles.wrapper}>
        {Object.keys(groupedData).map((groupingKey, index) => {
          const groupingValue: Array<{ releaseAt: string }> =
            groupedData[groupingKey];
          const releaseAt = dayjs(groupingValue[0].releaseAt).toDate();

          if (groupingKey === '発売日未定') {
            return (
              <li key={index} className={styles.itemWrapper}>
                <div className={styles.itemHeader}>
                  <span className={styles.headerValueNoDate}>COMING SOON</span>
                </div>
                <Items
                  items={groupedData[groupingKey]}
                  rowNumber={rowNumber}
                  userAgent={userAgent}
                  mode={mode}
                  onStarClick={handleStarClick}
                />
              </li>
            );
          }

          return (
            <li key={index} className={styles.itemWrapper}>
              <div className={styles.itemHeader}>
                <span className={styles.headerValue}>
                  {releaseAt.getMonth() + 1}
                </span>
                月
                <span className={styles.headerValue}>
                  {releaseAt.getDate()}
                </span>
                日<div></div>
              </div>
              <Items
                items={groupedData[groupingKey]}
                rowNumber={isDesktop ? 1 : rowNumber}
                userAgent={userAgent}
                mode={mode}
                onStarClick={handleStarClick}
              />
            </li>
          );
        })}
      </ul>
    );
  }, [
    eventShortcutSelected,
    groupedEvents,
    groupedLaunch,
    mode,
    rowNumber,
    userAgent,
    handleStarClick,
    isDesktop,
  ]);

  if (!groupedLaunch) return <Layout></Layout>;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta property="og:title" content={meta.title} key="title" />
      </Head>
      <Layout mode={mode}>
        <div className={styles.container}>
          <Menu
            rowNumber={rowNumber}
            setRowNumber={setRowNumber}
            props={{ mode }}
          />

          {shortcuts && (
            <ShortcutsBar
              shortcuts={shortcuts}
              onPress={onPressShortcut}
              onPressAll={clearShortcutFilter}
              onPressEvent={onPressEventShortcut}
              selected={activeShortcut?.id}
              hasEvent={events.length > 0}
              hasLottery={false}
              eventSelected={eventShortcutSelected}
              lotterySelected={false}
            />
          )}
          {!!activeShortcut && <ShortcutsResultTitle count={launchItemCount} />}

          {renderLaunchItem()}
          <div className={styles.loadingWrapper}>
            <h2 className={styles.dumbFooter} />
          </div>
        </div>
        {showModal && renderModal()}
      </Layout>
    </>
  );
};

export default Launch;
